<template>
  <base-modal
    :id="'make-offer-modal'"
    :title="'Make an offer'"
    @closeModal="onCloseModal"
  >
    <div class="purchase-modal-wrapper">
      <div class="content">
        <div class="row-wrapper">
          <div class="label">
            Item Name
          </div>
          <div>{{ name }}</div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Selling Method
          </div>
          <div>{{ getSellMethodByAddress() }}</div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Seller
          </div>
          <div class="address">
            {{ sellerAddress | blockchainAddress }}
          </div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Price
          </div>
          <div>
            {{ (price || 0) | formatWeiAmount }}
            {{ currency | transformToCurrencyName }}
          </div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Offer
          </div>
          <div>
            <input v-model="amount" placeholder="Amount" type="number" />{{
              currency | transformToCurrencyName
            }}
          </div>
        </div>
        <div class="row-wrapper">
          <div class="label">Final Price (% {{ tax }} Tax)</div>
          <div class="final-price">
            {{ amountWithTax }} {{ currency | transformToCurrencyName }}
          </div>
        </div>
        <div class="row-wrapper">
          <div class="label">
            Expiration Time (UTC)
          </div>
          <div>
            <v-date-picker
              ref="expirationTime"
              v-model="expirationTime"
              class="date-picker"
              mode="dateTime"
              :timezone="'UTC'"
              :min-date="new Date().valueOf()"
            >
              <template #default="{ inputValue, inputEvents }">
                <input
                  class="px-2 py-1 border rounded focus:outline-none focus:border-blue-300"
                  style="width: 180px"
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </template>
            </v-date-picker>
          </div>
        </div>
      </div>
      <div v-if="errorMsg" class="error">
        {{ errorMsg }}
      </div>
      <div class="footer">
        <primary-button
          class="confirm-btn"
          :is-disable="isDisable"
          @onClick="makeAnOffer"
        >
          <template v-if="isOffering">
            <b-icon icon="arrow-clockwise" animation="spin" font-scale="2" />
          </template>
          <template v-else>
            Confirm
          </template>
        </primary-button>
        <filter-option-btn
          class="cancel-btn"
          :is-disable="isOffering"
          @onClick="onCloseModal"
        >
          Cancel
        </filter-option-btn>
      </div>
    </div>
  </base-modal>
</template>
<script>
import PrimaryButton from '@/components/BaseComponents/PrimaryButton.vue';
import FilterOptionBtn from '@/components/BaseComponents/FilterOptionBtn.vue';
import BaseModal from '@/components/BaseModal.vue';
import {
  getNftTax,
  convertDecToWei,
  sleep,
  handleTransactionError,
  getSellMethod
} from '@/helpers/index';
import { offer } from '@/helpers/utils';
import BigNumber from 'bignumber.js';
import {
  getMarketPriceOfNft,
  getDutchAuctionFinalPrice
} from '@/helpers/utils';
import { mapGetters } from 'vuex';
import { MARKET_ADDRESSES } from '@/constans';
import { ValidateException, HttpException } from '@/exceptions';

export default {
  components: {
    PrimaryButton,
    FilterOptionBtn,
    BaseModal
  },
  props: {
    marketContract: {
      type: String,
      default: ''
    },
    nft: {
      type: Object,
      default: () => ({})
    },
    sellerAddress: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      amount: '',
      isOffering: false,
      price: '',
      currency: '',
      expirationTime: ''
    };
  },
  computed: {
    ...mapGetters('myAccount', ['myBalances']),
    errorMsg() {
      if (Number(this.amount <= 0)) {
        return 'Amount must be greater than 0';
      }
      return '';
    },
    isDisable() {
      return (
        Number(this.amount <= 0) ||
        new Date(this.expirationTime).valueOf() <= new Date().valueOf()
      );
    },
    name() {
      return this.nft.name || '';
    },
    nftContract() {
      return this.nft.contract;
    },
    nftId() {
      return this.nft.id;
    },
    amountWithTax() {
      const tax = getNftTax(this.nftContract);
      const amountWithTax = +(this.amount * (1 + tax)).toFixed(2);
      return this.amount ? amountWithTax : '-';
    },
    tax() {
      return +getNftTax(this.nftContract) * 100;
    },
    isDutchAuctionMarket() {
      return this.marketContract === MARKET_ADDRESSES.dutchAuctionMarketplace;
    },
    initExpirationTime() {
      return new Date().valueOf() + 86400000;
    }
  },
  methods: {
    getSellMethod,
    convertDecToWei,
    getNftTax,
    handleTransactionError,
    onCloseModal() {
      if (this.isOffering) return;
      this.$bvModal.hide('make-offer-modal');
    },
    validate() {
      const expirationTimeData = Math.floor(
        new Date(this.expirationTime).valueOf() / 1000
      );
      const userBalance = this.myBalances[this.currency];
      const currencyTime = Math.floor(new Date().valueOf() / 1000);
      const totalOfferPriceInWei = convertDecToWei(
        this.amount * (1 + getNftTax(this.nftContract))
      );
      if (expirationTimeData <= currencyTime) {
        throw new ValidateException(
          'error',
          'Expiration Time must be greater than current time'
        );
      }
      if (new BigNumber(userBalance).comparedTo(totalOfferPriceInWei) < 0) {
        throw new ValidateException('error', 'Your balance is not enough!');
      }
    },
    async makeAnOffer() {
      const offerPriceInWei = convertDecToWei(this.amount);
      const expirationTimeData = Math.floor(
        new Date(this.expirationTime).valueOf() / 1000
      );

      this.validate();
      this.isOffering = true;
      try {
        const tx = await offer(
          this.nftContract,
          this.nftId,
          this.currency,
          offerPriceInWei,
          expirationTimeData,
          this.isDutchAuctionMarket
        );
        if (tx.transactionHash) {
          await sleep(9000);
          await this.$emit('handleRefreshData');
          this.$dialog.notify.success("You've offerd successfully!");
        } else {
          this.$dialog.notify.error('An error has occurred. Please try again!');
        }
      } catch (error) {
        this.isOffering = false;
        throw new HttpException(error);
      }
      this.isOffering = false;
      this.amount = '';
      (this.expirationTime = this.initExpirationTime), this.onCloseModal();
    },
    async fetchDutchAuctionFinalPrice() {
      if (!this.nftContract || !this.nftId || !this.isDutchAuctionMarket)
        return;
      const [price, currency] = await getDutchAuctionFinalPrice(
        this.nftContract,
        this.nftId
      );
      this.price = price.toString();
      this.currency = currency;
    },
    async fetchOnChainCurrentPrice() {
      if (!this.nftContract || !this.nftId) return;
      const [price, currency] = await getMarketPriceOfNft(
        this.nftContract,
        this.nftId
      );
      this.price = price;
      this.currency = currency;
    },
    init() {
      this.expirationTime = new Date().valueOf() + 86400000;
      if (this.isDutchAuctionMarket) {
        this.fetchDutchAuctionFinalPrice();
      } else {
        this.fetchOnChainCurrentPrice();
      }
    },
    getSellMethodByAddress() {
      if (this.isDutchAuctionMarket) {
        return 'Dutch Auction';
      }
      return 'Fixed Price';
    }
  }
};
</script>
<style lang="scss">
.error {
  color: red;
  margin-top: 20px;
  margin-left: 20px;
}
.content {
  padding: 0 20px;
  font-family: Inter-Medium;
  .row-wrapper {
    display: flex;
    justify-content: space-between;
    margin: 7px 0;
    height: 35px;
    .label {
      color: #798392;
      font-size: 16px;
      font-family: Inter-Regular;
      line-height: 35px;
    }
    .final-price {
      line-height: 35px;
    }
    .address {
      color: #00a3fe;
      line-height: 35px;
    }
    input {
      color: #798392;
      border: 1px solid #e3e6e9;
      border-radius: 6px;
      padding: 4px 10px;
      margin-right: 4px;
      width: 100px;
      &.expiration {
        width: 200px;
      }
    }
  }
}
.footer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0;

  .cancel-btn {
    width: 48%;
    margin-left: 5px;
    height: 48px;
  }
  .confirm-btn {
    width: 48%;
    height: 48px;
    margin-right: 5px;
  }
}
</style>
